// AUTO-GENERATED from servers/common-servers-v2/helpers/FormHelper/conditions.js by servers/common-servers-v2/scripts/sync-form-helper.js.
// Do not edit: change the source and run `node scripts/sync-form-helper.js`.
/* eslint-disable */
import { UNARY_OPERATORS } from "./constants.js";
import { normalize_digits } from "./normalize_fa.js";

/*
 * Conditions, shaped and named like panels/ui-examples generate-panel-form
 * (`condition_value`, `show_conditions`, `disabled_conditions`):
 *
 *   { first_field, first_value, operator, second_field, second_value }
 *
 * `first_field` / `second_field` hold the key of a form field (the panel stores
 * an object-path into the form, a flat key is its simplest case); when empty the
 * `*_value` constant is used. A list of conditions is AND; one item of the list
 * may be `{ or: [condition, ...] }` for an OR group. Nothing is ever eval'd.
 *
 * Differences from the panel function, on purpose:
 *   - option objects compare by their `.value`, arrays by their values (spec §6);
 *   - `equals` compares scalars by their string form and treats null / "" / []
 *     as the same "empty", because form values come from inputs;
 *   - `is_empty` / `is_not_empty` are new.
 */

const has_own = (object, key) => Object.prototype.hasOwnProperty.call(object, key);

// `form` is a plain { key: value } object or a getter function (path) => value.
const read_value = (form, path) => {
    if (typeof form === "function") {
        return form(path);
    }
    if (form && typeof form === "object" && typeof path === "string" && has_own(form, path)) {
        return form[path];
    }
    return undefined;
};

const comparable = (value) => {
    if (Array.isArray(value)) {
        return value.map(comparable);
    }
    if (value && typeof value === "object" && has_own(value, "value")) {
        return value.value;
    }
    return value;
};

const is_empty_value = (value) => value === undefined
    || value === null
    || (typeof value === "string" && value.trim() === "")
    || (Array.isArray(value) && value.length === 0);

const is_scalar = (value) => ["string", "number", "boolean"].includes(typeof value);

const scalar_equals = (a, b) => is_scalar(a) && is_scalar(b) && String(a) === String(b);

const equals = (first, second) => {
    const a = comparable(first);
    const b = comparable(second);
    if (is_empty_value(a) || is_empty_value(b)) {
        return is_empty_value(a) && is_empty_value(b);
    }
    if (Array.isArray(a) && Array.isArray(b)) {
        return a.length === b.length && a.every((item) => b.some((other) => scalar_equals(item, other)));
    }
    if (Array.isArray(a)) {
        return a.length === 1 && scalar_equals(a[0], b);
    }
    if (Array.isArray(b)) {
        return b.length === 1 && scalar_equals(a, b[0]);
    }
    return scalar_equals(a, b);
};

const to_number = (value) => {
    const number = typeof value === "number" ? value : parseFloat(normalize_digits(value));
    return Number.isNaN(number) ? NaN : number;
};

const includes = (first, second) => {
    const a = comparable(first);
    const b = comparable(second);
    if (is_empty_value(b)) {
        return false;
    }
    if (typeof a === "string") {
        return is_scalar(b) && a.includes(String(b));
    }
    if (Array.isArray(a)) {
        const contains = (item) => a.some((other) => scalar_equals(other, item));
        return Array.isArray(b) ? b.every(contains) : contains(b);
    }
    return false;
};

// One condition. Returns true / false; an unknown operator or a missing
// condition does not constrain anything (same as the panel function).
const evaluate_condition = (condition, form) => {
    if (!condition || typeof condition !== "object") {
        return true;
    }
    const first = condition.first_field ? read_value(form, condition.first_field) : condition.first_value;
    if (UNARY_OPERATORS.includes(condition.operator)) {
        const empty = is_empty_value(comparable(first));
        return condition.operator === "is_empty" ? empty : !empty;
    }
    const second = condition.second_field ? read_value(form, condition.second_field) : condition.second_value;
    switch (condition.operator) {
        case "equals":
            return equals(first, second);
        case "not_equals":
            return !equals(first, second);
        case "js_equals":
            return comparable(first) == comparable(second);
        case "js_not_equals":
            return comparable(first) != comparable(second);
        case "greater_than":
            return to_number(comparable(first)) > to_number(comparable(second));
        case "greater_than_or_equals":
            return to_number(comparable(first)) >= to_number(comparable(second));
        case "less_than":
            return to_number(comparable(first)) < to_number(comparable(second));
        case "less_than_or_equals":
            return to_number(comparable(first)) <= to_number(comparable(second));
        case "includes":
        case "in":
            return includes(first, second);
        case "not_includes":
        case "not_in":
            return !includes(first, second);
        default:
            return true;
    }
};

// Drop-in for generate-panel-form's `condition_value(condition, key, form)`:
// `key` is the running result and is only ever narrowed.
const condition_value = (condition, key, form) => Boolean(key) && evaluate_condition(condition, form);

// One element of a condition list: a condition or an `{ or: [...] }` group.
const evaluate_condition_item = (item, form) => {
    if (item && typeof item === "object" && Array.isArray(item.or)) {
        return item.or.length === 0 || item.or.some((condition) => evaluate_condition(condition, form));
    }
    return evaluate_condition(item, form);
};

// A condition list is AND. An empty or missing list is true.
const evaluate_conditions = (list, form) => {
    if (!Array.isArray(list)) {
        return true;
    }
    return list.every((item) => evaluate_condition_item(item, form));
};

// Every field key a condition (or `{ or }` group) reads.
const condition_field_refs = (item) => {
    if (!item || typeof item !== "object") {
        return [];
    }
    if (Array.isArray(item.or)) {
        return item.or.flatMap(condition_field_refs);
    }
    return [item.first_field, item.second_field].filter((key) => typeof key === "string" && key.length > 0);
};

export {
    condition_value,
    evaluate_condition,
    evaluate_condition_item,
    evaluate_conditions,
    condition_field_refs,
    is_empty_value,
    comparable,
    read_value,
};
